import { NgTemplateOutlet } from '@angular/common';
import { Component, DestroyRef, effect, inject, input, output, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatCard, MatCardContent, MatCardTitle } from '@angular/material/card';
import { MatDialog } from '@angular/material/dialog';
import { MatCell, MatCellDef, MatColumnDef, MatHeaderCell, MatHeaderCellDef, MatHeaderRow, MatHeaderRowDef, MatRow, MatRowDef, MatTable, MatTableDataSource } from '@angular/material/table';
import { MatTooltip } from '@angular/material/tooltip';
import { RestrictedElementDirective } from '@auth/directives';
import { APPLICATION_TYPE, EntityStatus, FormatNumberPipe } from '@core';
import { Store } from '@ngrx/store';
import { ConfirmationDialogComponent, TableZoomComponent } from '@ui';
import { NgxSkeletonLoaderModule } from 'ngx-skeleton-loader';
import { filter } from 'rxjs';

import { DevicesActions, DevicesSelectors } from '../../ngrx-store';
import { MeasurementUnitDisplayPipe } from '../../pipes';
import { DeviceService } from '../../services';
import { ChildRelationDevices, DeviceData, NormalizedPortTableItem } from './../../models';

@Component({
  imports: [
    NgxSkeletonLoaderModule,
    MatTable,
    MatCell,
    MatCellDef,
    MatColumnDef,
    MatHeaderCell,
    MatHeaderCellDef,
    MatHeaderRow,
    MatHeaderRowDef,
    MatRow,
    MatRowDef,
    RestrictedElementDirective,
    MatCard,
    MatCardContent,
    MatCardTitle,
    TableZoomComponent,
    MatTooltip,
    FormatNumberPipe,
    MeasurementUnitDisplayPipe,
    NgTemplateOutlet,
  ],
  selector: 'avs-switch-pdu-children-table',
  styleUrl: './switch-pdu-children-table.component.scss',
  templateUrl: './switch-pdu-children-table.component.html',
})
export class SwitchPduChildrenTableComponent {
  deviceId = input.required<string>();
  locationId = input.required<string>();
  isNetworkSwitch = input.required<boolean>();
  title = input<string>('Port List');
  predefinedChild = input<number>();
  childList = signal<ChildRelationDevices[] | undefined>(undefined);
  selectedPort = output<ChildRelationDevices>();
  portUpdated = output();
  protected isMobile = inject(APPLICATION_TYPE) === 'mobile';
  protected displayedColumns!: string[];
  protected zoom = signal<string | null>(null);
  protected tableId = 'device-children-table';
  protected dataSource = new MatTableDataSource<NormalizedPortTableItem>([]);
  protected readonly eEntityStatus = EntityStatus;
  private readonly actionColumnName = 'actionBar';
  private displayedColumnsInitialValue: string[] = [];
  private readonly switchColumns = [
    'index',
    'poePower',
    'speed',
    'activity',
    'profile',
    'packetsTx',
    'packetsRx',
    'txRate',
    'rxRate',
    'power',
    this.actionColumnName,
  ];
  private readonly outletColumns = [
    'index',
    'voltage',
    'current',
    'power',
    'powerOn',
  ];
  private deviceService = inject(DeviceService);
  private store = inject(Store);
  private dialog = inject(MatDialog);
  private destroyRef = inject(DestroyRef);

  constructor() {
    effect(() => {
      this.store.select(DevicesSelectors.getDeviceById(this.deviceId()))
        .pipe(
          filter(Boolean),
          takeUntilDestroyed(this.destroyRef),
        )
        .subscribe(device => {
          this.displayedColumnsInitialValue = this.isNetworkSwitch() ? this.switchColumns : this.outletColumns;
          this.childList.set(
            this.predefinedChild()
              ? device[this.isNetworkSwitch() ? 'portDevices' : 'outletDevices']?.filter(
                c => c.orderNumber === this.predefinedChild(),
              )
              : device[this.isNetworkSwitch() ? 'portDevices' : 'outletDevices'],
          );
          this.syncActionColumnWithDeviceStatus(device.status);
          this.drawAvailableColumns();
          this.normalizePortData();
        });
    });
  }

  protected selectPort(element: NormalizedPortTableItem) {
    const port = this.childList()?.find((port) => port.orderNumber === element.orderNumber);

    if (port !== undefined) {
      this.selectedPort.emit(port);
    }
  }

  protected resumePort(element: ChildRelationDevices) {
    ConfirmationDialogComponent.open(this.dialog, {
      description: `Are you sure you want to resume  port ${element.orderNumber} ?`,
      title: 'Resume',
    })
      .subscribe(confirmation => {
        if (confirmation) {
          this.updatePort(element.childDevice.id, { status: EntityStatus.Active });
        }
      });

  }

  protected pausePort(element: ChildRelationDevices) {
    ConfirmationDialogComponent.open(this.dialog, {
      description: `Are you sure you want to pause port ${element.orderNumber} ?`,
      title: 'Pause',
    })
      .subscribe(confirmation => {
        if (confirmation) {
          this.updatePort(element.childDevice.id, { status: EntityStatus.Paused });
        }
      });

  }

  private syncActionColumnWithDeviceStatus(status: EntityStatus) {
    const actionCol = this.actionColumnName;
    const idx = this.displayedColumnsInitialValue.indexOf(actionCol);

    if (status !== EntityStatus.Active) {
      if (idx >= 0) {
        this.displayedColumnsInitialValue.splice(idx, 1);
      }

      return;
    }

    if (idx < 0 && this.isNetworkSwitch()) {
      this.displayedColumnsInitialValue.push(actionCol);
    }
  }

  private updatePort(deviceId: string, data: Partial<DeviceData>) {
    this.deviceService.updateDeviceData({
      data,
      deviceId,
      locationId: this.locationId(),
    }).subscribe({
      next: () => {
        this.store.dispatch(DevicesActions.loadDeviceDetails({ deviceId: this.deviceId(), locationId: this.locationId() }));
      },
    });
  }

  private normalizePortData() {
    const normalizedPortsItems: any = [];

    this.childList()?.forEach(port => {
      const normalizedAttributes: { [key: string]: any; } = {};

      port.childDevice.additionalAttributes.forEach(attribute => {
        normalizedAttributes[attribute.name] = { unitsOfMeasure: attribute.measurementUnit, value: attribute.value };
      });

      const normalizedPort = {
        ...port,
        childDevice: {
          ...port.childDevice,
          activity: {
            rx: this.calculateActivity(normalizedAttributes['speed']?.value, normalizedAttributes['rxRate']?.value),
            tx: this.calculateActivity(normalizedAttributes['speed']?.value, normalizedAttributes['txRate']?.value),
          },
          additionalAttributes: normalizedAttributes,
        },
      };

      normalizedPortsItems.push(normalizedPort);
    });

    this.dataSource.data = normalizedPortsItems;
  }

  private calculateActivity(speed: string, activity: number) {
    const speedNumber = parseInt(speed, 10) / 100;

    if (!speedNumber || !activity) {
      return 0;
    }

    return activity / speedNumber;
  }

  private drawAvailableColumns() {
    this.displayedColumns = [...this.displayedColumnsInitialValue];

    const availableAttributeNames = this.getAvailableAttributeNames();

    if (!availableAttributeNames) {
      this.displayedColumns = ['index', 'empty'];

      return;
    }

    if (this.isNetworkSwitch()) {
      this.displayedColumns = this.filterColumnsForNetworkSwitch(this.displayedColumns, availableAttributeNames);

      return;
    }
    this.displayedColumns = this.buildOrderedOutletColumns(availableAttributeNames);
  }

  private getAvailableAttributeNames(): Set<string> | null {
    const firstChild = this.childList()?.[0];
    const attrs = firstChild?.childDevice?.additionalAttributes;

    if (!attrs?.length) return null;

    return new Set(attrs.map(a => a.name));
  }

  private filterColumnsForNetworkSwitch(currentColumns: string[], availableNames: Set<string>): string[] {
    const ALWAYS_ALLOWED_COLUMNS = new Set(['index', 'actionBar']);

    if (availableNames.has('txRate') || availableNames.has('rxRate')) {
      ALWAYS_ALLOWED_COLUMNS.add('activity');
    }

    return currentColumns.filter(col => ALWAYS_ALLOWED_COLUMNS.has(col) || availableNames.has(col));
  }

  private buildOrderedOutletColumns(availableNames: Set<string>): string[] {
    const orderedDataColumns = this.outletColumns.filter(col => col !== 'index' && availableNames.has(col));

    return orderedDataColumns.length ? ['index', ...orderedDataColumns] : ['index', 'empty'];
  }
}
